/**
 * RealAgentBuilderService — HTTP implementation against wg-agents-studio-api.
 *
 * Bound via the AGENT_BUILDER_SERVICE token when environment.useMockData is
 * false (always in prod). Every IAgentBuilderService method is real — the
 * localStorage mock fallback is gone (W5/W6 landed server-side):
 *
 *   - published reads come from GET /agents/published[/{agent_id}]
 *   - deploy tracking via POST /agents/drafts/{id}/refresh-status
 *   - smoke tests invoke the LIVE runtime (POST .../smoke-test)
 *   - editPublished spawns a server-side revision draft
 *
 * The one deliberate gap: deletePublished — decommissioning a live runtime
 * isn't automated yet, so it fails with a clear message instead of
 * pretending (mock mirrors this).
 *
 * The studio-api wraps every response in { success, message, body }; this
 * service unwraps `.body`. The ErrorInterceptor preserves HTTP `status`, so we
 * map 404 -> NotFoundError and 409 -> ConflictError (P1-G17 optimistic lock).
 *
 * P1-G1 parity: methods mirror MockAgentBuilderService's response shapes
 * exactly; change both together.
 */

import { HttpBackend, HttpClient, HttpEventType } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of, throwError, timer } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  exhaustMap,
  last,
  map,
  retry,
  switchMap,
  takeWhile,
  tap,
  timeout,
} from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import {
  AbilityRequest,
  AgentRunsResult,
  AnthropicToolSchema,
  CoBuilderChatMessage,
  CoBuilderDocument,
  CoBuilderResult,
  CoBuilderStreamEvent,
  CoBuilderTurn,
  ComingSoonAbility,
  ConflictError,
  DirectoryAgent,
  Draft,
  DraftCodegen,
  DraftTestChatResult,
  DraftTestRun,
  GatewayTarget,
  GroupOption,
  IAgentBuilderService,
  KnowledgeDocument,
  KnowledgeDocumentList,
  KnowledgeUploadTicket,
  NotFoundError,
  PublishResult,
  PublishedAgent,
  TransferOwnerResult,
  TransferRef,
  OwnerHistory,
  ReportTemplatePreset,
  ReportTemplateResult,
  EmailTemplateResult,
  ScheduleState,
  SmokeTestResult,
  TestChatStreamEvent,
  ToolCatalogResult,
  ValidationResult,
  WizardInput,
  WizardPrompt,
} from './agent-builder.interface';

interface ApiEnvelope<T> {
  success: boolean;
  message: string;
  body: T;
}

/** A published-states draft projected to the card/list shape the UI renders. */
function toPublishedAgent(d: Draft): PublishedAgent {
  return {
    agent_id: d.inputs.identity.agent_id,
    display_name: d.inputs.identity.display_name,
    description: d.inputs.identity.description,
    department: d.inputs.identity.department,
    color: d.inputs.identity.color,
    state: d.state,
    pr1_url: d.pr1_url,
    pr2_url: d.pr2_url,
    last_activity: d.last_modified,
    submitted_by: d.submitted_by ?? d.created_by,
    owner: d.created_by,
    approver: d.approver,
    error_context: d.error_context,
    environments: d.inputs.environments,
  };
}

@Injectable({ providedIn: 'root' })
export class RealAgentBuilderService implements IAgentBuilderService {
  private readonly http = inject(HttpClient);
  /** Interceptor-free client for the presigned S3 PUT ONLY. Built straight on
   *  HttpBackend so the AuthInterceptor never runs: attaching Authorization to
   *  a SigV4-presigned URL breaks its signature (S3 403s), and the local-dev
   *  URL rewrite must not touch it either. Everything else (mint / confirm /
   *  list / delete) goes through the normal authed `http` client. */
  private readonly bareHttp = new HttpClient(inject(HttpBackend));

  /** e.g. https://api.../wgagentsstudio/agents — base-path mapping strips wgagentsstudio. */
  private readonly base = `${environment.BASE_URL}${environment.STUDIO_API.AGENTS_BASE}`;
  /** Catalog/abilities routes sit beside /agents (…/wgagentsstudio/tools, /abilities/*). */
  private readonly toolsBase = this.base.replace(/\/agents$/, '');

  // ── helpers ──────────────────────────────────────────────────────────

  private unwrap<T>(obs: Observable<ApiEnvelope<T>>): Observable<T> {
    return obs.pipe(
      map((res) => res.body),
      catchError((err) => this.mapError(err)),
    );
  }

  /** Silently retry TRANSIENT failures (network drop, gateway 502/504, Bedrock
   *  throttle/timeout 503) with a short jittered backoff before surfacing the
   *  error. Applied only to the AI-assist generation routes — those are
   *  stateless server-side, so re-sending is safe; state-changing routes
   *  (submit, publish, …) must never auto-retry. 429 is deliberately NOT
   *  retried: hammering a rate limit only extends it. */
  private retryTransient<T>(obs: Observable<T>): Observable<T> {
    const TRANSIENT = [0, 502, 503, 504];
    return obs.pipe(
      retry({
        count: 2,
        delay: (err, retryCount) => {
          const status = (err as { status?: number })?.status ?? 0;
          if (!TRANSIENT.includes(status)) return throwError(() => err);
          return timer(1500 * retryCount + Math.random() * 500);
        },
      }),
    );
  }

  private mapError(err: { status?: number; message?: string; error?: ApiEnvelope<unknown> }): Observable<never> {
    if (err?.status === 404) {
      return throwError(() => new NotFoundError(err.message || 'Not found'));
    }
    if (err?.status === 409) {
      const body = (err.error?.body ?? {}) as { currentVersion?: number; currentState?: string };
      return throwError(
        () => new ConflictError(
          // err.message is already the ErrorInterceptor's resolved detail
          // (it prefers the nested body.message over the generic top-level
          // "error" placeholder this API returns for handler errors) — read
          // it first rather than re-deriving from the raw envelope.
          err.message || err.error?.message || 'Conflict',
          body.currentVersion ?? 0,
          (body.currentState as Draft['state']) ?? 'draft',
        ),
      );
    }
    return throwError(() => err);
  }

  // ── Drafts (real) ────────────────────────────────────────────────────

  listDrafts(): Observable<Draft[]> {
    return this.unwrap(this.http.get<ApiEnvelope<Draft[]>>(`${this.base}/drafts`));
  }

  getDraft(id: string): Observable<Draft> {
    return this.unwrap(this.http.get<ApiEnvelope<Draft>>(`${this.base}/drafts/${id}`));
  }

  createDraft(initial?: Partial<WizardInput>): Observable<Draft> {
    return this.unwrap(this.http.post<ApiEnvelope<Draft>>(`${this.base}/drafts`, initial ?? {}));
  }

  updateDraft(id: string, patch: Partial<WizardInput>, expectedVersion: number): Observable<Draft> {
    return this.unwrap(
      this.http.put<ApiEnvelope<Draft>>(`${this.base}/drafts/${id}`, { patch, expectedVersion }),
    );
  }

  deleteDraft(id: string): Observable<void> {
    return this.unwrap(this.http.delete<ApiEnvelope<null>>(`${this.base}/drafts/${id}`)).pipe(
      map(() => undefined as void),
    );
  }

  validateDraft(id: string): Observable<ValidationResult> {
    return this.unwrap(this.http.post<ApiEnvelope<ValidationResult>>(`${this.base}/drafts/${id}/validate`, {}));
  }

  previewDraft(id: string): Observable<Record<string, string>> {
    return this.unwrap(
      this.http.post<ApiEnvelope<Record<string, string>>>(`${this.base}/drafts/${id}/preview`, {}),
    );
  }

  // ── Gateway tool catalog (W1) ────────────────────────────────────────

  /** Full /tools payload: targets + the server-owned `presentation`
   *  arrangement contract (absent on an old API — callers fall back to the
   *  bundled category order). */
  listToolCatalog(): Observable<ToolCatalogResult> {
    return this.unwrap(
      this.http.get<ApiEnvelope<ToolCatalogResult & { knowledge_base_granted?: boolean }>>(
        `${this.toolsBase}/tools`),
    ).pipe(map((body) => ({
      targets: body.targets ?? [],
      presentation: body.presentation,
      // Server sends snake_case; surface the authoritative KB grant.
      knowledgeBaseGranted: body.knowledge_base_granted,
    })));
  }

  listTools(): Observable<GatewayTarget[]> {
    return this.listToolCatalog().pipe(map((catalog) => catalog.targets));
  }

  /** Server-owned report starter presets. Empty ⇒ callers use the bundled copy. */
  listReportTemplates(): Observable<ReportTemplatePreset[]> {
    return this.unwrap(
      this.http.get<ApiEnvelope<{ presets: ReportTemplatePreset[] }>>(
        `${this.toolsBase}/report-templates`,
      ),
    ).pipe(map((body) => body.presets ?? []));
  }

  listGroups(): Observable<GroupOption[]> {
    return this.unwrap(
      this.http.get<ApiEnvelope<{ groups: GroupOption[] }>>(`${this.base}/groups`),
    ).pipe(map((body) => body.groups ?? []));
  }

  // ── Abilities library (roadmap + requests) ───────────────────────────
  // Sibling of /tools — same base-minus-/agents. Coming-soon is a curated
  // studio-api list; requests are durably recorded server-side.

  listComingSoon(): Observable<ComingSoonAbility[]> {
    return this.unwrap(
      this.http.get<ApiEnvelope<{ abilities: ComingSoonAbility[] }>>(
        `${this.toolsBase}/abilities/coming-soon`,
      ),
    ).pipe(map((body) => body.abilities ?? []));
  }

  requestAbility(req: AbilityRequest): Observable<{ status: string }> {
    return this.unwrap(
      this.http.post<ApiEnvelope<{ status: string }>>(`${this.toolsBase}/abilities/requests`, req),
    );
  }

  // ── Knowledge base (draft-scoped RAG documents) ──────────────────────
  // Draft-scoped like every other drafts route. The S3 PUT is the ONE call
  // in this service that bypasses the interceptor chain (see `bareHttp`).

  private knowledgeBase(draftId: string): string {
    return `${this.base}/drafts/${encodeURIComponent(draftId)}/knowledge`;
  }

  listKnowledgeDocs(draftId: string): Observable<KnowledgeDocumentList> {
    return this.unwrap(
      this.http.get<ApiEnvelope<KnowledgeDocumentList>>(`${this.knowledgeBase(draftId)}/documents`),
    ).pipe(map((body) => ({ documents: body.documents ?? [], quota: body.quota })));
  }

  /** Mint ticket → PUT the raw bytes to the presigned URL → confirm.
   *  Emits the confirmed document (status usually 'queued'); per-doc
   *  ingestion progress comes from listKnowledgeDocs polling. */
  uploadKnowledgeFile(
    draftId: string,
    file: File,
    onProgress?: (fraction: number) => void,
  ): Observable<KnowledgeDocument> {
    const kb = this.knowledgeBase(draftId);
    const contentType = file.type || 'application/octet-stream';
    return this.unwrap(
      this.http.post<ApiEnvelope<KnowledgeUploadTicket>>(`${kb}/uploads`, {
        filename: file.name,
        content_type: contentType,
        size_bytes: file.size,
      }),
    ).pipe(
      switchMap((ticket) =>
        // Bare client: no Authorization (breaks the SigV4 signature), no URL
        // rewrite. Content-Type must match what the presign signed; any extra
        // signed headers arrive on the ticket and are echoed verbatim.
        this.bareHttp
          .put(ticket.upload_url, file, {
            headers: { 'Content-Type': contentType, ...(ticket.headers ?? {}) },
            reportProgress: true,
            observe: 'events',
            responseType: 'text',
          })
          .pipe(
            tap((event) => {
              if (event.type === HttpEventType.UploadProgress) {
                const total = event.total ?? file.size;
                if (total > 0) onProgress?.(Math.min(1, event.loaded / total));
              }
            }),
            last(), // wait for the response event — the PUT is fully done
            map(() => ticket),
          ),
      ),
      switchMap((ticket) =>
        this.unwrap(
          this.http.post<ApiEnvelope<{ document: KnowledgeDocument }>>(
            `${kb}/uploads/${encodeURIComponent(ticket.doc_id)}/confirm`,
            {},
          ),
        ),
      ),
      map((body) => body.document),
    );
  }

  deleteKnowledgeDoc(draftId: string, docId: string): Observable<void> {
    return this.unwrap(
      this.http.delete<ApiEnvelope<{ deleted: boolean }>>(
        `${this.knowledgeBase(draftId)}/documents/${encodeURIComponent(docId)}`,
      ),
    ).pipe(map(() => undefined as void));
  }

  // ── AI-assist (real Bedrock — live since Phase 1.14) ─────────────────

  /** Hero prompt-generation turn: POST queues it on the same async worker
   *  coBuilderStream uses (no API Gateway 29s ceiling — the richer output
   *  schema, including report format/sections, needs the headroom), then GET
   *  polls every ~500ms until the terminal frame. Falls back to the
   *  synchronous /ai-assist/prompt route when the streaming route isn't
   *  deployed/allowlisted yet, same recovery rule as coBuilderStream. */
  aiAssistPrompt(id: string, description: string, equippedAbilities?: string[]): Observable<WizardPrompt> {
    const body = {
      user_description: description,
      ...(equippedAbilities?.length ? { equipped_abilities: equippedAbilities } : {}),
    };
    const syncFallback = (): Observable<WizardPrompt> =>
      this.unwrap(this.http.post<ApiEnvelope<WizardPrompt>>(`${this.base}/drafts/${id}/ai-assist/prompt`, body));

    return this.unwrap(
      this.http.post<ApiEnvelope<{ stream_id: string | null; result?: WizardPrompt }>>(
        `${this.base}/drafts/${id}/ai-assist/prompt-stream`,
        body,
      ),
    ).pipe(
      switchMap((start) => {
        if (!start.stream_id) {
          // Server short-circuited (empty description) — already a finished turn.
          return of(start.result ?? ({} as WizardPrompt));
        }
        const poll = `${this.base}/drafts/${id}/ai-assist/prompt-stream/${start.stream_id}`;
        return timer(350, 500).pipe(
          exhaustMap(() =>
            this.retryTransient(
              this.unwrap(
                this.http.get<ApiEnvelope<{ status: string; result?: WizardPrompt; error?: string }>>(poll),
              ),
            ),
          ),
          takeWhile((ev) => ev.status !== 'done' && ev.status !== 'failed', true),
          last(),
          switchMap((ev) =>
            ev.status === 'done' && ev.result
              ? of(ev.result)
              : throwError(() => new Error(ev.error || 'AI assist failed — please try again.')),
          ),
          timeout(150_000),
        );
      }),
      // Route-unavailable → run the turn synchronously instead of failing it
      // (same fallback rule as coBuilderStream — see its comment for the full
      // 403/405/0 rationale around authorizer allowlist rollout lag).
      catchError((err) => {
        const status = (err as { status?: number })?.status;
        const streamingUnavailable =
          err instanceof NotFoundError || status === 0 || status === 403 || status === 405;
        return streamingUnavailable ? syncFallback() : throwError(() => err);
      }),
    );
  }

  aiAssistTool(id: string, intent: string): Observable<AnthropicToolSchema> {
    return this.unwrap(
      this.http.post<ApiEnvelope<AnthropicToolSchema>>(`${this.base}/drafts/${id}/ai-assist/tool`, {
        tool_intent: intent,
      }),
    );
  }

  aiAssistReportTemplate(
    id: string,
    description: string,
    format: 'html' | 'pdf' = 'html',
  ): Observable<ReportTemplateResult> {
    return this.retryTransient(
      this.unwrap(
        this.http.post<ApiEnvelope<ReportTemplateResult>>(
          `${this.base}/drafts/${id}/ai-assist/report-template`,
          { description, format },
        ),
      ),
    );
  }

  aiAssistEmailTemplate(id: string, description: string): Observable<EmailTemplateResult> {
    return this.retryTransient(
      this.unwrap(
        this.http.post<ApiEnvelope<EmailTemplateResult>>(
          `${this.base}/drafts/${id}/ai-assist/email-template`,
          { description },
        ),
      ),
    );
  }

  coBuilder(
    id: string,
    message: string,
    inputs: WizardInput,
    history: CoBuilderTurn[],
    document?: CoBuilderDocument,
    readiness?: string[],
    testOutput?: string,
  ): Observable<CoBuilderResult> {
    return this.retryTransient(
      this.unwrap(
        this.http.post<ApiEnvelope<CoBuilderResult>>(`${this.base}/drafts/${id}/ai-assist/cobuilder`, {
          message,
          inputs,
          history,
          ...(document ? { document } : {}),
          ...(readiness?.length ? { readiness } : {}),
          ...(testOutput ? { test_output: testOutput } : {}),
        }),
      ),
    );
  }

  /** Streaming co-builder turn: POST queues the turn on an async worker Lambda
   *  (no API Gateway 29s ceiling), then GET polls the growing reply every
   *  ~500ms until the terminal frame ('done' with the full result, or
   *  'failed'). exhaustMap skips ticks while a poll is in flight; transient
   *  poll failures ride the same silent-retry backoff as the sync route. Falls
   *  back to the synchronous route when the streaming route isn't deployed
   *  (404) so the UI never regresses during rollout. */
  coBuilderStream(
    id: string,
    message: string,
    inputs: WizardInput,
    history: CoBuilderTurn[],
    readiness?: string[],
    testOutput?: string,
  ): Observable<CoBuilderStreamEvent> {
    const syncFallback = (): Observable<CoBuilderStreamEvent> =>
      this.coBuilder(id, message, inputs, history, undefined, readiness, testOutput).pipe(
        map((result) => ({ status: 'done' as const, text: result.reply ?? '', result })),
      );
    return this.unwrap(
      this.http.post<ApiEnvelope<{ stream_id: string | null; result?: CoBuilderResult }>>(
        `${this.base}/drafts/${id}/ai-assist/cobuilder-stream`,
        {
          message,
          inputs,
          history,
          ...(readiness?.length ? { readiness } : {}),
          ...(testOutput ? { test_output: testOutput } : {}),
        },
      ),
    ).pipe(
      switchMap((start) => {
        if (!start.stream_id) {
          // Server short-circuited (e.g. help text) — already a finished turn.
          return of<CoBuilderStreamEvent>({
            status: 'done',
            text: start.result?.reply ?? '',
            result: start.result,
          });
        }
        const poll = `${this.base}/drafts/${id}/ai-assist/cobuilder-stream/${start.stream_id}`;
        return timer(350, 500).pipe(
          exhaustMap(() =>
            this.retryTransient(
              this.unwrap(this.http.get<ApiEnvelope<CoBuilderStreamEvent>>(poll)),
            ),
          ),
          distinctUntilChanged((a, b) => a.status === b.status && a.text === b.text),
          takeWhile((ev) => ev.status !== 'done' && ev.status !== 'failed', true),
          timeout(150_000),
        );
      }),
      // Route-unavailable → run the turn synchronously instead of failing it.
      // This must cover MORE than a clean 404: when the streaming routes exist
      // on API Gateway but the Lambda authorizer's endpoint-permissions
      // allowlist doesn't know them yet (config lag — exactly what broke the
      // co-builder on 2026-07-02), the gateway's 403 carries no CORS headers,
      // so the browser reports status 0. 403/405/0 on this stream therefore
      // mean "streaming path unavailable", and the sync route (long-allowlisted)
      // is the right recovery. 429 is deliberately NOT here — falling back on a
      // rate limit would double-spend the budget.
      catchError((err) => {
        const status = (err as { status?: number })?.status;
        const streamingUnavailable =
          err instanceof NotFoundError || status === 0 || status === 403 || status === 405;
        return streamingUnavailable ? syncFallback() : throwError(() => err);
      }),
    );
  }

  getCoBuilderChat(id: string): Observable<CoBuilderChatMessage[]> {
    return this.unwrap(
      this.http.get<ApiEnvelope<{ messages: CoBuilderChatMessage[] }>>(
        `${this.base}/drafts/${id}/ai-assist/cobuilder-chat`,
      ),
    ).pipe(map((body) => body.messages ?? []));
  }

  saveCoBuilderChat(id: string, messages: CoBuilderChatMessage[]): Observable<CoBuilderChatMessage[]> {
    return this.unwrap(
      this.http.post<ApiEnvelope<{ messages: CoBuilderChatMessage[] }>>(
        `${this.base}/drafts/${id}/ai-assist/cobuilder-chat`,
        { messages },
      ),
    ).pipe(map((body) => body.messages ?? []));
  }

  // ── Approval state machine (real — W4a) ──────────────────────────────

  submitDraft(id: string): Observable<Draft> {
    return this.unwrap(this.http.post<ApiEnvelope<Draft>>(`${this.base}/drafts/${id}/submit`, {}));
  }

  withdrawDraft(id: string): Observable<Draft> {
    return this.unwrap(this.http.post<ApiEnvelope<Draft>>(`${this.base}/drafts/${id}/withdraw`, {}));
  }

  listApprovals(): Observable<Draft[]> {
    return this.unwrap(this.http.get<ApiEnvelope<Draft[]>>(`${this.base}/approvals`));
  }

  approveDraft(id: string, comment: string): Observable<Draft> {
    return this.unwrap(this.http.post<ApiEnvelope<Draft>>(`${this.base}/drafts/${id}/approve`, { comment }));
  }

  rejectDraft(id: string, comment: string): Observable<Draft> {
    return this.unwrap(this.http.post<ApiEnvelope<Draft>>(`${this.base}/drafts/${id}/reject`, { comment }));
  }

  // Publish: render + push a branch to wg-agents-studio-runtimes (W4b). Retry
  // re-publishes (the branch push is idempotent — fast-forwards the ref).
  publishDraft(id: string): Observable<PublishResult> {
    return this.unwrap(this.http.post<ApiEnvelope<PublishResult>>(`${this.base}/drafts/${id}/publish`, {}));
  }
  retryPublish(id: string): Observable<PublishResult> {
    return this.unwrap(this.http.post<ApiEnvelope<PublishResult>>(`${this.base}/drafts/${id}/publish`, {}));
  }

  // ── W5: deploy tracking + published surface (real) ───────────────────

  refreshStatus(id: string): Observable<Draft> {
    return this.unwrap(this.http.post<ApiEnvelope<Draft>>(`${this.base}/drafts/${id}/refresh-status`, {}));
  }

  /** Admin escape hatch — server refuses unless the runtime ARN exists. */
  markResolvedManually(id: string): Observable<Draft> {
    return this.unwrap(this.http.post<ApiEnvelope<Draft>>(`${this.base}/drafts/${id}/mark-resolved`, {}));
  }

  /** Promote a live agent to prod (admin): server opens the prod PRs and
   *  records prod in the agent's environments, returning the updated draft. */
  promoteToProd(id: string): Observable<Draft> {
    return this.unwrap(this.http.post<ApiEnvelope<Draft>>(`${this.base}/drafts/${id}/promote`, {}));
  }

  listPublished(): Observable<PublishedAgent[]> {
    return this.unwrap(this.http.get<ApiEnvelope<Draft[]>>(`${this.base}/published`)).pipe(
      map((drafts) => (drafts ?? []).map(toPublishedAgent)),
    );
  }

  /** Team directory: live published agents the caller can access (their own +
   *  ones granted via user/group allowed_agents; admins see all). The server
   *  returns card-safe rows directly — no projection needed. */
  listDirectory(): Observable<DirectoryAgent[]> {
    return this.unwrap(
      this.http.get<ApiEnvelope<DirectoryAgent[]>>(`${this.base}/directory`),
    ).pipe(map((agents) => agents ?? []));
  }

  /** Server-side this also advances the deploy status (throttled) — the
   *  detail page's poll drives pr_open → building → live with no extra call. */
  getPublishedDraft(agentId: string): Observable<Draft> {
    return this.unwrap(
      this.http.get<ApiEnvelope<Draft>>(`${this.base}/published/${encodeURIComponent(agentId)}`),
    );
  }

  editPublished(agentId: string): Observable<Draft> {
    return this.unwrap(
      this.http.post<ApiEnvelope<Draft>>(`${this.base}/published/${encodeURIComponent(agentId)}/edit`, {}),
    );
  }

  transferOwner(ref: TransferRef, newOwner: string, dryRun = false): Observable<TransferOwnerResult> {
    const url = ref.kind === 'draft'
      ? `${this.base}/drafts/${encodeURIComponent(ref.draftId)}/transfer-owner`
      : `${this.base}/published/${encodeURIComponent(ref.agentId)}/transfer-owner`;
    return this.http
      .post<ApiEnvelope<TransferOwnerResult>>(url, dryRun ? { owner: newOwner, dry_run: true } : { owner: newOwner })
      .pipe(
        map((res) => res.body),
        // A refused transfer is a 400 whose body IS the readiness report
        // (ok:false + checks). Resolve it so the dialog can show every failing
        // rule instead of one flattened message; other errors map as usual.
        catchError((err: { status?: number; error?: ApiEnvelope<TransferOwnerResult> }) => {
          const body = err?.error?.body;
          if (err?.status === 400 && body && Array.isArray(body.checks)) {
            return of({ ...body, ok: false });
          }
          return this.mapError(err);
        }),
      );
  }

  getOwnerHistory(agentId: string): Observable<OwnerHistory> {
    return this.unwrap(
      this.http.get<ApiEnvelope<OwnerHistory>>(
        `${this.base}/published/${encodeURIComponent(agentId)}/owner-history`,
      ),
    );
  }

  deletePublished(agentId: string): Observable<void> {
    // Decommissioning a live runtime (remove files + IAM + pipeline) isn't
    // automated yet — fail honestly instead of pretending.
    return throwError(() => new Error(
      `Decommissioning isn't automated yet — ask the platform team to remove `
      + `singleshot/${agentId.toLowerCase()} via PR.`,
    ));
  }

  // ── W6: smoke test against the LIVE runtime ──────────────────────────

  smokeTest(agentId: string, message: string): Observable<SmokeTestResult> {
    return this.unwrap(
      this.http.post<ApiEnvelope<SmokeTestResult>>(
        `${this.base}/published/${encodeURIComponent(agentId)}/smoke-test`, { message },
      ),
    );
  }

  /** Live draft preview: runs the real agentic loop (Bedrock + gateway tools)
   *  server-side without deploying. `inputs` carries unsaved wizard edits. */
  testChatDraft(
    id: string,
    message: string,
    history: unknown[],
    inputs?: WizardInput,
  ): Observable<DraftTestChatResult> {
    return this.unwrap(
      this.http.post<ApiEnvelope<DraftTestChatResult>>(`${this.base}/drafts/${id}/test-chat`, {
        message,
        history,
        inputs,
      }),
    );
  }

  /** Async live test-chat: POST queues the turn on a worker Lambda (300s
   *  budget — tool-heavy questions no longer die at API Gateway's ~29s), then
   *  GET polls tool progress + the final answer every ~700ms. Falls back to
   *  the synchronous route when streaming is unavailable (missing route or
   *  authorizer-allowlist lag → 404/403, or a CORS-stripped status 0), so
   *  callers never regress during rollout. */
  testChatDraftStream(
    id: string,
    message: string,
    history: unknown[],
    inputs?: WizardInput,
  ): Observable<TestChatStreamEvent> {
    const syncFallback = (): Observable<TestChatStreamEvent> =>
      this.testChatDraft(id, message, history, inputs).pipe(
        map((result) => ({ status: 'done' as const, text: result.response ?? '', result })),
      );
    return this.unwrap(
      this.http.post<ApiEnvelope<{ stream_id: string | null }>>(
        `${this.base}/drafts/${id}/test-chat-stream`,
        { message, history, inputs },
      ),
    ).pipe(
      switchMap((start) => {
        if (!start.stream_id) return syncFallback();
        const poll = `${this.base}/drafts/${id}/test-chat-stream/${start.stream_id}`;
        return timer(600, 700).pipe(
          exhaustMap(() =>
            this.retryTransient(
              this.unwrap(this.http.get<ApiEnvelope<TestChatStreamEvent>>(poll)),
            ),
          ),
          distinctUntilChanged((a, b) => a.status === b.status && a.text === b.text),
          takeWhile((ev) => ev.status !== 'done' && ev.status !== 'failed', true),
          timeout(320_000), // the worker's budget is 300s
        );
      }),
      catchError((err) => {
        const status = (err as { status?: number })?.status;
        const streamingUnavailable =
          err instanceof NotFoundError || status === 0 || status === 403 || status === 405;
        return streamingUnavailable ? syncFallback() : throwError(() => err);
      }),
    );
  }

  /** Complex agents: queue an async codegen run (202 + queued state). The
   *  worker Lambda writes the result onto the draft — poll getDraft. */
  generateCode(id: string, feedback?: string): Observable<DraftCodegen> {
    return this.unwrap(
      this.http.post<ApiEnvelope<DraftCodegen>>(`${this.base}/drafts/${id}/generate-code`, {
        feedback: feedback ?? '',
      }),
    );
  }

  // ── Automation operations ─────────────────────────────────────────────

  /** Preview run for an automation draft (202 + queued state). The worker
   *  Lambda writes the report onto the draft's test_run — poll getDraft. */
  testRunDraft(id: string): Observable<DraftTestRun> {
    return this.unwrap(
      this.http.post<ApiEnvelope<DraftTestRun>>(`${this.base}/drafts/${id}/test-run`, {}),
    );
  }

  listRuns(agentId: string): Observable<AgentRunsResult> {
    return this.unwrap(
      this.http.get<ApiEnvelope<AgentRunsResult>>(
        `${this.base}/published/${encodeURIComponent(agentId)}/runs`,
      ),
    );
  }

  runNow(agentId: string): Observable<{ accepted: boolean; message?: string }> {
    return this.unwrap(
      this.http.post<ApiEnvelope<{ accepted: boolean; message?: string }>>(
        `${this.base}/published/${encodeURIComponent(agentId)}/run-now`, {},
      ),
    );
  }

  setScheduleState(agentId: string, enabled: boolean): Observable<{ schedule_state: ScheduleState }> {
    return this.unwrap(
      this.http.post<ApiEnvelope<{ schedule_state: ScheduleState }>>(
        `${this.base}/published/${encodeURIComponent(agentId)}/schedule-state`, { enabled },
      ),
    );
  }

  /** GET .../runs/{run_id}/report-url → { url }. Mints a fresh presigned link
   *  for an expired report (the "Get fresh link" action on the runs card). */
  refreshReportUrl(agentId: string, runId: string): Observable<{ url: string }> {
    return this.unwrap(
      this.http.get<ApiEnvelope<{ url: string }>>(
        `${this.base}/published/${encodeURIComponent(agentId)}/runs/${encodeURIComponent(runId)}/report-url`,
      ),
    );
  }
}
